import { BB } from '../../../bb/bb';
import { LANG } from '../../../language/language';
import { GalleryStore } from '../../storage/gallery-store';
import { TKlProjectMeta } from '../../kl-types';
import { timestampToAge } from '../utils/timestamp-to-age';
import { showModal } from './base/showModal';
import { THEME } from '../../../theme/theme';
import removeLayerImg from 'url:/src/app/img/ui/remove-layer.svg';

export type TGalleryUiParams = {
    galleryStore: GalleryStore;
    onLoad: (id: string) => void;
    ignoreId?: string; // e.g. current project, to highlight it or something (optional)
};

export class GalleryUi {
    private readonly rootEl: HTMLElement;
    private readonly gridEl: HTMLElement;
    private readonly galleryStore: GalleryStore;
    private readonly onLoad: (id: string) => void;
    private params: TGalleryUiParams;

    private async renderGrid() {
        this.gridEl.innerHTML = '';
        const list = await this.galleryStore.getProjectList();

        if (list.length === 0) {
            this.gridEl.textContent = LANG('file-storage-empty');
            BB.css(this.gridEl, {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                color: '#888'
            });
            return;
        }

        BB.css(this.gridEl, {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gridGap: '20px',
            alignItems: 'start'
        });

        for (const meta of list) {
            const isCurrent = this.params.ignoreId === meta.projectId;

            const card = BB.el({
                className: 'kl-gallery-card',
                css: {
                    background: BB.isDark() ? '#333' : '#eee',
                    borderRadius: '5px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.1s ease',
                    border: isCurrent ? '2px solid #4a90e2' : '2px solid transparent'
                },
                onClick: () => {
                    this.onLoad(meta.projectId);
                    this.close();
                }
            });

            // Hover effect logic handled by CSS or listeners
            card.onmouseenter = () => card.style.borderColor = isCurrent ? '#4a90e2' : '#888';
            card.onmouseleave = () => card.style.borderColor = isCurrent ? '#4a90e2' : 'transparent';

            const thumbEl = BB.el({
                css: {
                    width: '100%',
                    aspectRatio: '1',
                    background: `url(${meta.thumbnail instanceof Blob ? URL.createObjectURL(meta.thumbnail) : (meta.thumbnail as any).toDataURL?.() || ''}) no-repeat center`,
                    backgroundSize: 'cover'
                    //backgroundColor: '#fff' // checkboard?
                }
            });

            const infoEl = BB.el({
                css: {
                    padding: '8px',
                    fontSize: '12px'
                },
                content: [
                    BB.el({
                        textContent: meta.name || 'Untitled',
                        css: {
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            marginBottom: '2px',
                            display: 'block'
                        }
                    }),
                    BB.el({
                        textContent: timestampToAge(meta.timestamp),
                        css: { color: '#888' }
                    }),
                ]
            });

            const deleteBtn = BB.el({
                tagName: 'button',
                title: LANG('file-storage-clear'),
                content: `<img src="${removeLayerImg}" style="width: 16px; height: 16px; display: block; filter: invert(1)">`,
                css: {
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    background: 'rgba(0,0,0,0.5)',
                    border: 'none',
                    borderRadius: '3px',
                    padding: '4px',
                    cursor: 'pointer',
                    opacity: '0',
                    transition: 'opacity 0.2s'
                },
                onClick: (e) => {
                    e.stopPropagation();
                    this.confirmDelete(meta.projectId);
                }
            });

            const renameBtn = BB.el({
                tagName: 'button',
                title: 'Rename',
                content: '✏️',
                css: {
                    position: 'absolute',
                    top: '5px',
                    right: '35px',
                    background: 'rgba(0,0,0,0.5)',
                    border: 'none',
                    borderRadius: '3px',
                    padding: '4px 6px',
                    cursor: 'pointer',
                    opacity: '0',
                    transition: 'opacity 0.2s',
                    fontSize: '14px'
                },
                onClick: async (e) => {
                    e.stopPropagation();
                    const newName = prompt('Enter new name:', meta.name || 'Untitled');
                    if (newName && newName !== meta.name) {
                        await this.galleryStore.updateProjectName(meta.projectId, newName);
                        this.renderGrid();
                    }
                }
            });

            card.onmouseover = () => {
                deleteBtn.style.opacity = '1';
                renameBtn.style.opacity = '1';
            };
            card.onmouseout = () => {
                deleteBtn.style.opacity = '0';
                renameBtn.style.opacity = '0';
            };

            BB.append(card, [thumbEl, infoEl, deleteBtn, renameBtn]);
            this.gridEl.append(card);
        }
    }

    private confirmDelete(id: string) {
        showModal({
            target: document.body,
            type: 'warning',
            message: LANG('file-storage-clear-prompt'),
            buttons: [LANG('file-storage-clear'), 'Cancel'],
            deleteButtonName: LANG('file-storage-clear'),
            callback: async (result) => {
                if (result === LANG('file-storage-clear')) {
                    await this.galleryStore.deleteProject(id);
                    this.renderGrid();
                }
            }
        });
    }

    private close() {
        this.rootEl.remove();
    }

    constructor(p: TGalleryUiParams) {
        this.galleryStore = p.galleryStore;
        this.onLoad = p.onLoad;
        this.params = p;

        this.gridEl = BB.el({
            css: {
                maxHeight: '60vh',
                overflowY: 'auto',
                minHeight: '200px',
                padding: '10px'
            }
        });

        this.rootEl = BB.el({
            css: {
                position: 'fixed',
                left: '0',
                top: '0',
                right: '0',
                bottom: '0',
                zIndex: '1000',
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            },
            onClick: (e) => {
                if (e.target === this.rootEl) this.close();
            }
        });

        const panel = BB.el({
            className: 'kl-popup', // reuse existing popup style
            css: {
                width: '800px',
                maxWidth: '90vw',
                background: BB.isDark() ? '#222' : '#fff',
                color: BB.isDark() ? '#eee' : '#222',
                borderRadius: '10px',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 5px 20px rgba(0,0,0,0.5)'
            }
        });

        const header = BB.el({
            content: 'Gallery',
            css: {
                padding: '15px',
                borderBottom: '1px solid #444',
                fontSize: '20px',
                fontWeight: 'bold',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }
        });

        const closeBtn = BB.el({
            tagName: 'button',
            content: '×',
            css: {
                background: 'none',
                border: 'none',
                color: 'inherit',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '0 10px'
            },
            onClick: () => this.close()
        });
        header.append(closeBtn);

        panel.append(header, this.gridEl);
        this.rootEl.append(panel);

        document.body.append(this.rootEl);
        this.renderGrid();
    }
}
